<template>
    <q-card>
        <q-card-section  class="q-pb-sm">
            <div class="text-h6 text-center">Upload a Polygon</div>
        </q-card-section>
        <q-card-section  class="q-pt-none">
            <div class="text-body2 q-pa-sm">
                <ul>
                    <li>Maximum polygon size supported is approximately 5,000 mi<sup>2</sup>  (about the size of Connecticut)</li>
                    <li>Multiple polygons are supported and will be treated as a single entity (values reported will be for all the aggregated polygons).</li>
                    <li>Multi-part polygons are not supported. If you have a multi-part polygon, first split it into individual polygons using <a class="myLink" href='https://pro.arcgis.com/en/pro-app/latest/tool-reference/data-management/multipart-to-singlepart.htm' target='_blank'>ArcGIS</a> or <a class="myLink" href='https://docs.qgis.org/testing/en/docs/user_manual/processing_algs/qgis/vectorgeometry.html#qgismultiparttosingleparts' target='_blank'>QGIS</a>.  The results will be aggregated for all polygons.</li>
                    <li>Overly complex poylgons may create errors.  Simplifying polygons in <a class="myLink" href="https://pro.arcgis.com/en/pro-app/3.1/tool-reference/cartography/simplify-polygon.htm" target="_blank">ArcGIS</a> or <a class="myLink" href="https://docs.qgis.org/3.34/en/docs/training_manual/processing/generalize.html" target="_blank">QGIS</a> by removing extraneous vertices can help address these errors</li>
                </ul>  
            </div>
        </q-card-section>
        <q-separator class="q-ma-md"/>
        <q-card-section class="q-pt-none" >
            <div class="text-body1 q-pa-sm">Upload a shapefile polygon.<q-btn dense size="xs" flat class="gray q-mb-md" icon="help_outline" @click="infoDialogs['shapefile'].model = !infoDialogs['shapefile'].model" aria-label="Shapefile upload help"/></div>
            <div class="row justify-center q-pa-sm">
                <q-file filled bottom-slots 
                    v-model="pickedFile" 
                    label="Upload Zipped Shapefile" 
                    counter 
                    accept=".zip"
                    style="min-width: 325px"
                >
                    <template v-slot:prepend>
                        <q-icon name="cloud_upload" @click.stop.prevent />
                    </template>
                    <template v-slot:append>
                        <q-icon name="close" @click.stop.prevent="pickedFile = null" class="cursor-pointer" />
                    </template>
                    <template v-slot:hint>
                        Include all component files in zip: .shp .dbf .shx .prj
                    </template>
                </q-file>
            </div>
            <q-separator class="q-ma-md"/>
            <div class="text-body1 q-pa-sm">Upload a GeoJson polygon. <q-btn dense size="xs" flat class="gray q-mb-md" icon="help_outline" @click="infoDialogs['geojson'].model = !infoDialogs['geojson'].model" aria-label="Geojson upload help"/></div>
            <div class="row justify-center q-pa-sm">
                <q-file filled bottom-slots 
                    v-model="pickedJsonFile" 
                    label="Upload GeoJson" 
                    counter 
                    accept=".json, .geojson"
                    style="min-width: 325px"
                >
                    <template v-slot:prepend>
                        <q-icon name="cloud_upload" @click.stop.prevent />
                    </template>
                    <template v-slot:append>
                        <q-icon name="close" @click.stop.prevent="pickedJsonFile = null" class="cursor-pointer" />
                    </template>
                    <template v-slot:hint>
                        
                    </template>
                </q-file>
            </div>
            
        </q-card-section>
        <q-card-actions align="right">
            <q-btn label="Start Analysis" color="primary" v-close-popup aria-label="OK" @click="onShpUpload()" />
            <q-btn label="Cancel"  v-close-popup aria-label="Close" />
        </q-card-actions>
    </q-card>
    <div v-for="(infoObj, name) in infoDialogs" :key="name" >
        <q-dialog v-model="infoObj.model">
            <InfoDialog  :html="infoObj.text" :name="infoObj.pName"/>
        </q-dialog>
    </div>
</template>
<script setup lang="ts">
import { ref, watch } from "vue";
import { useMapItems } from "../composables/useMapItems"
import * as turf from '@turf/turf'
import InfoDialog from "./InfoDialog.vue";
import { useHelpers } from "../composables/useHelpers"
const { getNextVersionNumber, map, setUserPolygon, cellData,  toggleLayer, tileLayers, setActivePane, zoomToPolygon, testInBounds, setProcessedData } = useMapItems()
const { arrayFindObjectByProp, logGAevent } = useHelpers()
const onShpUpload = async () => {
        
    //it's a shapefile being uploaded
    if (pickedFile.value){ 
        logGAevent("shp_upload", "", "")
        const worker = new Worker('shp-worker.js');
        // console.log(pickedFile)
        const file = pickedFile.value;
        // console.log(file)

        // convert the file to arrayBuffer & post to worker
        const reader = new FileReader();
        reader.onload = (event) => {
            // console.log(event)
            const arrayBuffer = event.target.result;
            worker.postMessage(arrayBuffer);
        };

        reader.readAsArrayBuffer(file);
        worker.onmessage = (event) => {
            // console.log(event)
            const version = getNextVersionNumber();
            const userPoly = makeCleanJson(event.data, version)
            zoomToPolygon(userPoly) 


                // and with all new polys always start with flags
                const layer = arrayFindObjectByProp(tileLayers.value, 'id', "FLAGS")
                toggleLayer(true, layer, null)
                const inBounds = testInBounds()
                if (inBounds == 'straddle'){infoDialogs.value.straddle.model = true}
                else if (inBounds == 'outside'){infoDialogs.value.outside.model = true}
                setProcessedData(version, 'inBounds', null, null, inBounds)
                setActivePane('summary')


        };
    }
    else if (pickedJsonFile.value){  //uplaoded file is GeoJSON
        logGAevent("geojson_upload", "", "")
        // set symbology & make empty graphics layer
        const reader = new FileReader();
        reader.onload = async (event) => {
            // console.log(event)
            const content = JSON.parse(event.target.result as any) ;

            const version = getNextVersionNumber();
            const userPoly = makeCleanJson(content, version)
            zoomToPolygon(userPoly) 

            const layer = arrayFindObjectByProp(tileLayers.value, 'id', "FLAGS")
            toggleLayer(true, layer, null)
            const inBounds = testInBounds()
            if (inBounds == 'straddle'){infoDialogs.value.straddle.model = true}
            else if (inBounds == 'outside'){infoDialogs.value.outside.model = true}
            setProcessedData(version, 'inBounds', null, null, inBounds)
            setActivePane('summary')


        };
        reader.readAsText(pickedJsonFile.value, "UTF-8");
    };


}


const makeCleanJson = (content, version) =>{
    // console.log(content)
    
    const userPoly = {
        type: "FeatureCollection",
        features:[],
        id: `userPoly_${version}`
    } as any;

    content['features'].forEach((feat)=>{
        userPoly.features.push(feat)
    })
    // console.log(userPoly)
    setUserPolygon(userPoly, version)
    return userPoly
}

const pickedFile = ref(null)
const pickedJsonFile = ref(null)


watch(pickedFile, (nv, ov) =>{
    if (nv != null){pickedJsonFile.value = null;}
})

watch(pickedJsonFile, (nv, ov) =>{
    if (nv != null){pickedFile.value = null;}
})

const infoDialogs = ref({
  shapefile: {
    pName: "Shapefile Help",
    model: false,
        text: "All components of the <a href='https://www.loc.gov/preservation/digital/formats/fdd/fdd000280.shtml' target='_blank'>shapefile</a> must be included (.shp, .dbf, .shx, .prj, etc) in a .zip file.  The individual file should be directly zipped into the .zip file, not nested in any subfolder. Complicated polygons with many vertices use more memory and can create errors.  Simplifying the polygon in <a href='https://pro.arcgis.com/en/pro-app/3.1/tool-reference/cartography/simplify-polygon.htm' target='_blank'>ArcGIS</a> or <a href='https://docs.qgis.org/3.34/en/docs/training_manual/processing/generalize.html' target='_blank'>QGIS</a> can improve performance and reduce errors for complex polygons."
  },
  geojson: {
    pName: "GeoJSON Help",
    model: false,
      text: "GeoJSON files should conform to <a href='https://datatracker.ietf.org/doc/html/rfc7946' target='_blank'>GeoJSON Specifications</a>. Coordinates can either be in Web Mercator (EPSG 3857) or WGS84 (EPSG 4326). Complicated polygons with many vertices use more memory and can create errors.  Simplifying the polygon in <a href='https://pro.arcgis.com/en/pro-app/3.1/tool-reference/cartography/simplify-polygon.htm' target='_blank'>ArcGIS</a> or <a href='https://docs.qgis.org/3.34/en/docs/training_manual/processing/generalize.html' target='_blank'>QGIS</a> can improve performance and reduce errors for complex polygons."
  },
  sizeError:{
    pName: "Size Error",
    model: false,
    text: "Too big!"
  },
  straddle: {
    pName: "Partially Outside Study Area",
    model: false,
    text: "The Area of Interest entered is partially outside the study area.  Results are based on the portion of the AOI within the study area."
  },
  outside: {
    pName: "Outside Study Area",
    model: false,
    text: "The Area of Interest entered is outside the study area.  Please enter another AOI."
  },
})

</script>